import { useEffect, useState, type ChangeEvent } from "react"
import RequestCard from "./RequestCard"
import useEventRequestsValues from "../hooks/useEventRequestsValues"
import useEditEvent from "../../../../../shared/hooks/useEditEvent"
import useEventValuesForEdit from "../hooks/useEventValuesForEdit"
import { Request } from "./RequestCard"
import ConfirmModal from "../../../../../shared/components/UI/ConfirmModal"

const states = [
    {
        "id": "announced",
        "name": "Анонсировано",
    },
    {
        "id": "registration_opened",
        "name": "Идет регистрация"
    },
    {
        "id": "registration_closed",
        "name": "Регистрация закрыта"
    },
    {
        "id": "in_progress",
        "name": "В разгаре"
    },
    {
        "id": "completed",
        "name": "Завершено"
    },
    {
        "id": "cancelled",
        "name": "Отменено"
    }
]

const sliderStates = states.filter(state => state.id !== "cancelled")
const statusSliderPointPercents = [0, 24.4, 53.5, 77.3, 100]

const AdministrationEventForm = ({
    slug
}: {
    slug?: string
}) => {
    const [isActiveTab, setIsActiveTab] = useState<'all' | 'accepted'>('all')
    const [searchElem, setSearchElem] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const [sliderPercent, setSliderPercent] = useState(0)
    const [isCompleteConfirmOpen, setIsCompleteConfirmOpen] = useState(false)
    const [isDraggingStatus, setIsDraggingStatus] = useState(false)

    const { data } = useEventRequestsValues(slug)

    const { data: eventInfo, isLoading } = useEventValuesForEdit(slug)

    const { mutate: changeStateMutate, isPending: isChangeStatePending } = useEditEvent(slug)

    const eventState = eventInfo ? eventInfo.state : ''

    const canDeleteRequests = eventState !== 'completed'

    const teamsRequests = Object.values(
        (data?.registrations ?? []).reduce((acc: any, req: any) => {
            if (req.registration_type !== 'team') return acc

            const slug = req.team.slug

            acc[slug] ??= {
                team: req.team,
                registration_status: req.registration_status,
                registration_type: 'team',
                users: [],
                desired_event_party: req.desired_event_party
            }

            acc[slug].users.push(req.user)

            return acc
        }, {})
    )

    const allRequests = [
        ...(data?.registrations?.filter((req: any) => req.registration_type === 'user') ?? []),
        ...teamsRequests
    ].filter(req => req.registration_status !== 'rejected')

    const filteredRequests =
        allRequests.filter((request: any) =>
            `${request.team?.name} ${request.user?.username} ${request.user?.callsign}`
                .toLowerCase().includes(searchElem.toLowerCase())
        )

    const currentState = states.find(state => state.id === eventState)

    const currentSliderIndex = sliderStates.findIndex(state => state.id === eventState)

    const getPercentByIndex = (index: number) => {
        return statusSliderPointPercents[index] ?? 0
    }

    const getIndexByPercent = (percent: number) => {
        let closestIndex = 0

        statusSliderPointPercents.forEach((point, index) => {
            const closestDistance = Math.abs(statusSliderPointPercents[closestIndex] - percent)
            const currentDistance = Math.abs(point - percent)

            if (currentDistance < closestDistance) {
                closestIndex = index
            }
        })

        return closestIndex
    }

    const activeSliderIndex = getIndexByPercent(sliderPercent)

    const currentStatusName = currentState?.name

    const resetSliderToCurrentStatus = () => {
        if (currentSliderIndex === -1) return

        setSliderPercent(getPercentByIndex(currentSliderIndex))
    }

    useEffect(() => {
        if (currentSliderIndex !== -1) {
            setSliderPercent(getPercentByIndex(currentSliderIndex))
        }
    }, [currentSliderIndex])

    const isStatusSliderDisabled = isLoading || isChangeStatePending || isCompleteConfirmOpen || eventState === "completed" || currentSliderIndex === -1

    const prepareStatusData = (state: string) => {
        return {
            ...eventInfo,
            state,
        }
    }

    const handleChangeStatus = (newIndex: number) => {
        if (!eventInfo || isStatusSliderDisabled) return

        const newState = sliderStates[newIndex]

        if (!newState) {
            resetSliderToCurrentStatus()
            return
        }

        setSliderPercent(getPercentByIndex(newIndex))

        if (newState.id === eventState) return

        if (newState.id === "completed") {
            setIsCompleteConfirmOpen(true)
            return
        }

        changeStateMutate(
            prepareStatusData(newState.id),
            {
                onError: resetSliderToCurrentStatus
            }
        )
    }

    const handleSliderChange = (event: ChangeEvent<HTMLInputElement>) => {
        setSliderPercent(Number(event.target.value))
    }

    const handleSliderCommit = (percent: number) => {
        if (isStatusSliderDisabled) return

        const finalIndex = getIndexByPercent(percent)

        setIsDraggingStatus(false)
        setSliderPercent(getPercentByIndex(finalIndex))

        handleChangeStatus(finalIndex)
    }

    const handleCancelComplete = () => {
        setIsCompleteConfirmOpen(false)
        resetSliderToCurrentStatus()
    }

    const handleConfirmComplete = () => {
        if (!eventInfo) return

        setIsCompleteConfirmOpen(false)

        changeStateMutate(
            prepareStatusData("completed"),
            {
                onError: resetSliderToCurrentStatus
            }
        )
    }

    const emptyContent = currentState?.id === 'announced' ?
        'Переключите статус мероприятия для открытия регистрации' :
        'На данный момент никто не подал регистрацию на мероприятие'
    return (

        <div className="manageEventModal__administrationForm">
            <div className="manageEventModal__administrationForm-title">
                <h1>Статус мероприятия</h1>
                <p>Статус можно изменить, перетащив ползунок на нужный этап</p>

                <div className={`manageEventModal__statusSlider ${isStatusSliderDisabled ? "disabled" : ""} ${isDraggingStatus ? "dragging" : ""}`}>
                    <div className="manageEventModal__statusSlider-track">
                        <div
                            className="manageEventModal__statusSlider-fill"
                            style={{ width: `${sliderPercent}%` }}
                        />

                        {sliderStates.map((state, index) => (
                            <span
                                key={state.id}
                                className={`manageEventModal__statusSlider-dot ${index <= activeSliderIndex ? "passed" : ""} ${index === activeSliderIndex ? "active" : ""}`}
                                style={{ left: `${getPercentByIndex(index)}%` }}
                            />
                        ))}

                        <span
                            className="manageEventModal__statusSlider-thumb"
                            style={{ left: `${sliderPercent}%` }}
                        />

                        <input
                            type="range"
                            className="manageEventModal__statusSlider-range"
                            min={0}
                            max={100}
                            step={1}
                            value={sliderPercent}
                            disabled={isStatusSliderDisabled}
                            onChange={handleSliderChange}
                            onPointerDown={() => setIsDraggingStatus(true)}
                            onPointerUp={(event) => handleSliderCommit(Number(event.currentTarget.value))}
                            onPointerCancel={() => {
                                setIsDraggingStatus(false)
                                resetSliderToCurrentStatus()
                            }}
                            onKeyUp={(event) => handleSliderCommit(Number(event.currentTarget.value))}
                            aria-label="Изменить статус мероприятия"
                        />
                    </div>

                    <div className="manageEventModal__statusSlider-labels">
                        {sliderStates.map((state, index) => (
                            <span
                                key={state.id}
                                className={`manageEventModal__statusSlider-label ${index === activeSliderIndex ? "active" : ""}`}
                                style={{ left: `${getPercentByIndex(index)}%` }}
                            >
                                {state.name}
                            </span>
                        ))}
                    </div>

                    {currentStatusName && (
                        <p className="manageEventModal__statusSlider-current">
                            Текущий этап: <span>{currentStatusName}</span>
                        </p>
                    )}
                </div>
            </div>
            {filteredRequests ?
                <>
                    <div className="eventManagementModal__requestsList">
                        <div className="eventManagementModal__requestsList-header">
                            <div className="eventManagementModal__requestsList-header-tabs">
                                <button
                                    className={`eventManagementModal__requestsList-header-tabs-tab ${isActiveTab === 'all' ? 'active' : ''}`}
                                    onClick={() => setIsActiveTab('all')}
                                >
                                    Все
                                </button>
                                <button
                                    className={`eventManagementModal__requestsList-header-tabs-tab ${isActiveTab === 'accepted' ? 'active' : ''}`}
                                    onClick={() => setIsActiveTab('accepted')}
                                >
                                    Принято
                                </button>
                            </div>
                            <p>
                                {"Количество заявок: " +
                                    (isActiveTab === 'all' ?
                                        (filteredRequests?.length)
                                        :
                                        (filteredRequests?.filter((request: any) => request.registration_status === 'approved').length))
                                }
                            </p>
                        </div>
                        <div className="eventManagementModal__searchWrapper">
                            <input
                                type="search"
                                className="eventManagementModal__search"
                                value={searchElem}
                                placeholder="Поиск"
                                onInput={({ target }) => setSearchElem((target as HTMLInputElement).value)}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}
                            />
                            {!isFocused && searchElem.length == 0 && (
                                <svg className='eventManagementModal__search-searchIcon' width="25" height="22" viewBox="0 0 25 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <rect width="1" height="22" rx="0.5" fill="#28282C" />
                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M16 5C13.5147 5 11.5 7.01472 11.5 9.5C11.5 11.9853 13.5147 14 16 14C17.241 14 18.3646 13.4977 19.1787 12.6853C19.1808 12.6831 19.183 12.6809 19.1852 12.6787C19.1869 12.677 19.1886 12.6753 19.1903 12.6737C19.9997 11.86 20.5 10.7384 20.5 9.5C20.5 7.01472 18.4853 5 16 5ZM20.7422 13.1763C21.5306 12.1608 22 10.8852 22 9.5C22 6.18629 19.3137 3.5 16 3.5C12.6863 3.5 10 6.18629 10 9.5C10 12.8137 12.6863 15.5 16 15.5C17.3879 15.5 18.6657 15.0288 19.6822 14.2376L23.7193 18.28C24.012 18.5731 24.4869 18.5734 24.78 18.2807C25.0731 17.988 25.0734 17.5131 24.7807 17.22L20.7422 13.1763Z" fill="#E5E4DF" />
                                </svg>
                            )}
                            {!!(searchElem.length > 0) && (
                                <button
                                    type="button"
                                    className="eventManagementModal__search-closeIcon"
                                    onClick={() => setSearchElem('')}
                                >
                                    <img src='/UI/closeModal.svg' />
                                </button>
                            )}
                        </div>
                        {searchElem.length == 0 ?
                            <div className="eventManagementModal__requestsList-list">
                                {isActiveTab === 'all' && filteredRequests?.map((item: Request, index: number) => (
                                    <RequestCard key={index} requestData={item} eventSlug={slug} canDelete={canDeleteRequests} />
                                ))}
                                {isActiveTab === 'accepted' && filteredRequests
                                    ?.filter((item: Request) => item.registration_status === 'approved')
                                    .map((item: Request, index: number) => (
                                        <RequestCard key={index} requestData={item} eventSlug={slug} canDelete={canDeleteRequests} />
                                    ))}
                                {/* {isActiveTab === 'accepted' && filtredTeamRequests
                                        ?.filter((item: teamRegistration) => item.users[0].registration_status === 'approved')
                                        .map((item: teamRegistration, index: number) => (
                                            <RequestCard key={index} type="team" teamRequest={item} eventSlug={slug} />
                                        ))} */}
                            </div>
                            :
                            <div className="eventManagementModal__empty">
                                <h1>Заявки не найдены</h1>
                            </div>
                        }
                    </div>
                    {/* <Button
                            className='eventManagementModal__autoBalance'
                            contrastStyle
                        >
                            Автобаланс сторон
                        </Button> */}
                </>
                :
                <div className="eventManagementModal__empty">
                    <h1>Список заявок пуст</h1>
                    <p>{emptyContent}</p>
                </div>}

            {isCompleteConfirmOpen && (
                <ConfirmModal
                    text="Завершение мероприятия"
                    description="Вы уверены, что хотите завершить мероприятие? Данное действие невозможно будет отменить."
                    confirmText="Завершить"
                    onConfirm={handleConfirmComplete}
                    closeModal={handleCancelComplete}
                    isPending={isChangeStatePending}
                />
            )}
        </div>
    )
}

export default AdministrationEventForm